/**
 * Formatage partagé de dates et de durées.
 *
 * Le serveur envoie des horodatages ISO 8601 ; l'affichage en français est une responsabilité du
 * frontend, jamais du backend (`docs/agent-spec/05-api-backend.md` réserve le français aux messages
 * d'erreur, pas aux dates, qui restent au format d'échange).
 *
 * Toutes les dates affichées le sont en heure de Tahiti, explicitement — jamais dans le fuseau du
 * navigateur de qui consulte l'écran. L'exploitation se passe en Polynésie française ; un
 * administrateur consultant l'application depuis la métropole ne doit pas voir une heure décalée de
 * ce qui se passe réellement sur le terrain.
 */

const FUSEAU_HORAIRE = "Pacific/Tahiti";

export function formaterDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("fr-FR", {
    timeZone: FUSEAU_HORAIRE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formaterDateSeule(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString("fr-FR", { timeZone: FUSEAU_HORAIRE });
}

/**
 * Date et heure sans les minutes (« 20/08/2026 14h »), pour les étiquettes sur la carte
 * (`EcranCarte.tsx`) : à cette échelle, la minute exacte de la dernière position n'apporte rien et
 * alourdirait une étiquette déjà partagée avec le numéro de GRV.
 */
export function formaterDateHeureSansMinutes(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const parties = new Intl.DateTimeFormat("fr-FR", {
    timeZone: FUSEAU_HORAIRE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const valeur = (type: string) => parties.find((partie) => partie.type === type)?.value ?? "";
  return `${valeur("day")}/${valeur("month")}/${valeur("year")} ${valeur("hour")}h`;
}

/**
 * Durée de présence dans une géozone, en jours décimaux plutôt qu'en minutes — l'information
 * logistique utile pour le suivi d'un GRV se lit en jours, pas en minutes.
 */
export function formaterDureeJours(minutes: number): string {
  const jours = minutes / 1_440;
  const valeur = jours.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
  return `${valeur} ${jours >= 2 ? "jours" : "jour"}`;
}

/**
 * Durée de présence détaillée (« 3j 19h 9min »), pour le tableau des séjours par géozone
 * (`OngletCarteGrv.tsx`) : contrairement à `formaterDureeJours`, une durée de quelques heures ou
 * minutes doit rester lisible, pas arrondie à « 0,0 jour ».
 */
export function formaterDureeDetaillee(minutes: number): string {
  const totalMinutes = Math.max(0, Math.round(minutes));
  const jours = Math.floor(totalMinutes / 1_440);
  const heures = Math.floor((totalMinutes % 1_440) / 60);
  const reste = totalMinutes % 60;
  return `${jours}j ${heures}h ${reste}min`;
}

/** Ancienneté relative, pour les alertes et la fiche de vie ("il y a 3 jours"). */
export function ilYA(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const minutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  const jours = Math.round(heures / 24);
  return `il y a ${jours} j`;
}
